/**
 * /logo-maker — логотип, который переживёт 16 пикселей.
 *
 * ⚠️ Логотип отказывает не на макете, а в жизни: во вкладке браузера, в кружке
 * аватара, на тёмной теме, на ксероксе в одну краску и у человека, который не
 * различает цвета. Поэтому рядом с большим предпросмотром стоит РЯД ПРОВЕРОК,
 * а не ещё один крупный квадрат. Замер рынка 12.09.2026: этого не делает
 * никто — ни Looka, ни Canva, ни Wix, ни Adobe Express, ни Squarespace.
 *
 * ⚠️ Буквы выгружаются контурами (см. render.js), поэтому страница НЕ грузит
 * веб-шрифты ради выдачи: и предпросмотр, и SVG, и PNG берут одни и те же
 * кривые. Веб-шрифт грузится ТОЛЬКО для подписи в списке шрифтов — там он и
 * нужен, чтобы название было набрано собой.
 *
 * ⚠️ Декоративный шрифт может не содержать нужного письма — у Bebas Neue нет
 * ни кириллицы, ни хангыля. Молча подставить другой значит показать человеку
 * не его логотип; поэтому подмена называется вслух.
 */

import { MARKS, MARK_CATS, monogramOf, searchMarks } from './marks.js';
import { buildScene, drawScene, sceneToSvg, LAYOUTS, sceneHasRaster } from './render.js';
import { PALETTES, contrast, cvdSafe, simulateCvd, paletteFromBrand, paletteFiles, hexToOklch, oklchToHex } from './palette.js';
import { parseSvg, traceRaster, rasterArt, fileToText } from './artwork.js';
import * as KIT from './kit.js';

const CFG = window.__LM_CFG || {};
const I18N = CFG.i18n || {};
const SLUG = 'logo-maker';
const $ = (id) => document.getElementById(id);
const t = (k, d) => I18N[k] || d || k;
export { PALETTES };

/* ── состояние ───────────────────────────────────────────────────────────── */

const DEFAULTS = {
    name: CFG.sampleName || 'Timbrica',
    name2: '',
    tagline: '',
    face: 'montserrat',
    bold: true,
    upper: false,
    track: 0,
    align: 'start',
    mark: 'ring',
    shape: 'none',
    layout: 'left',
    markScale: 1,
    gap: 0.26,
    palette: 'ink',
    brand: '',
    mode: 'auto',
    gradient: false,
    transparent: false,
    size: 1024,
};

let state = Object.assign({}, DEFAULTS);
let art = null;            // своя картинка: {kind, paths|src, vb, recolor}
let artName = '';

const glyphCache = new Map();
let scene = null;
let iconScene = null;
let faceNote = '';
const swapsReported = new Set();   // face_swapped — раз на пару «шрифт → замена» за страницу
let images = {};           // растровые картинки для холста, по href
let rebuildSeq = 0;

/* ── история ─────────────────────────────────────────────────────────────── */
/**
 * ⚠️ Undo здесь — не роскошь. Один щелчок по палитре или знаку заменяет
 * результат целиком, а вернуться «на глаз» невозможно: человек не помнит, какой
 * из двенадцати наборов стоял. Ни у одного конкурента в браузерном редакторе
 * отмены нет (замер 12.09.2026), и это самая дешёвая из недостающих вещей.
 */
const past = [];
const future = [];
let historyHold = false;

function snapshot() {
    return JSON.stringify({ s: state, a: art ? { n: artName, d: art } : null });
}
function pushHistory() {
    if (historyHold) return;
    const s = snapshot();
    if (past.length && past[past.length - 1] === s) return;
    past.push(s);
    if (past.length > 60) past.shift();
    future.length = 0;
    syncHistoryButtons();
}
function restore(json) {
    const o = JSON.parse(json);
    state = Object.assign({}, DEFAULTS, o.s);
    art = o.a ? o.a.d : null;
    artName = o.a ? o.a.n : '';
    historyHold = true;
    syncInputs();
    rebuild().then(() => { historyHold = false; });
}
function undo() {
    if (past.length < 2) return;
    future.push(past.pop());
    restore(past[past.length - 1]);
    syncHistoryButtons();
    ae('undo');
}
function redo() {
    if (!future.length) return;
    const s = future.pop();
    past.push(s);
    restore(s);
    syncHistoryButtons();
    ae('redo');
}
function syncHistoryButtons() {
    const u = $('lmUndo');
    const r = $('lmRedo');
    if (u) u.disabled = past.length < 2;
    if (r) r.disabled = !future.length;
}

/* ── контуры ─────────────────────────────────────────────────────────────── */
/**
 * Контуры строки выбранным шрифтом. Если шрифт не содержит этих букв —
 * берётся тот, что содержит, и об этом говорится на экране.
 */
async function glyphsFor(text, faceId, bold, track) {
    const clean = String(text || '').trim();
    if (!clean) return null;
    // ⚠️ Покрытие иероглифов и хангыля лежит в ОТДЕЛЬНОЙ таблице, и без неё
    // подбор шрифта уверенно отвечает «Noto Sans» на корейское название — а в
    // нём нет хангыля, и человек видит ряд пустых квадратов вместо логотипа.
    await window.PdfTextFont.ready(clean);
    let use = faceId;
    if (window.PdfTextFont.canRender && !window.PdfTextFont.canRender(clean, faceId)) {
        const picked = window.PdfTextFont.pick ? window.PdfTextFont.pick(clean) : 'noto';
        use = (typeof picked === 'string' ? picked : (picked && picked.id)) || 'noto';
        if (use !== faceId) {
            faceNote = t('face_swapped').replace(':face', labelOf(use));
            // Какое письмо не покрывают декоративные шрифты — это решение о том,
            // какой шрифт добавлять следующим. Раз на пару за страницу:
            // glyphsFor зовётся на каждую строку и каждую перерисовку, и событие
            // уходило сериями одинаковых (прод, 3 дня до 26.09.2026: 93 серии,
            // ни одной одиночной) — счёт перерисовок вместо счёта подмен.
            const swapKey = faceId + '>' + use;
            if (!swapsReported.has(swapKey)) {
                swapsReported.add(swapKey);
                ae('face_swapped', { from: faceId, to: use });
            }
        }
    }
    const key = use + '|' + (bold ? 'b' : 'r') + '|' + (track || 0) + '|' + clean;
    if (glyphCache.has(key)) return glyphCache.get(key);
    const g = await window.PdfTextFont.outline(use, clean, 100, { bold, track: track || 0 });
    glyphCache.set(key, g);
    if (glyphCache.size > 400) glyphCache.delete(glyphCache.keys().next().value);
    return g;
}

/**
 * ⚠️ Имя шрифта берётся из ПОЛНОГО списка, а не из того, что показан в выборе.
 * Подменяют надпись как раз на шрифты, которых в выборе нет (арабский, хангыль,
 * иероглифы), и фраза «набрано шрифтом :face» — единственная причина, по которой
 * подмена вообще объявляется вслух. С отфильтрованным списком она печатала
 * идентификатор: «набрано шрифтом arabic» (видно на арабской локали 12.09.2026).
 */
function labelOf(id) {
    const all = window.PdfTextFont.faces ? window.PdfTextFont.faces() : [];
    const f = all.find((x) => x.id === id);
    return (f && f.label) || id;
}
/** Шрифты, которые человек ВЫБИРАЕТ: письмо-специфичные подставляются сами. */
function faces() {
    return (window.PdfTextFont.faces ? window.PdfTextFont.faces() : [])
        .filter((f) => !/^(cjk|korean|arabic|japanese)$/.test(f.id));
}

/* ── цвета ───────────────────────────────────────────────────────────────── */
function basePalette() {
    if (state.brand) return paletteFromBrand(state.brand, state.mode);
    return PALETTES.find((x) => x.id === state.palette) || PALETTES[0];
}

function colors(over) {
    const p = basePalette();
    const o = over || {};
    const accent = (state.gradient && !o.flat)
        ? { g: [p.accent, gradientPartner(p.accent)] }
        : p.accent;
    const c = {
        shape: accent,
        markOn: p.on,
        mark: accent,
        text: p.ink,
        tag: p.tag,
        bg: p.bg,
    };
    if (o.grey) {
        // Оттенки серого — не «обесцветить на глаз», а та же яркость по WCAG:
        // логотип обязан сохранить в сером ту же разницу светлот, что в цвете.
        for (const k of ['markOn', 'text', 'tag']) c[k] = grey(p[k === 'markOn' ? 'on' : k === 'text' ? 'ink' : 'tag']);
        c.shape = grey(p.accent);
        c.mark = grey(p.accent);
        c.bg = grey(p.bg);
    }
    if (o.flat) { c.flat = o.flat; c.flatBg = o.flatBg || 'none'; }
    if (o.cvd) {
        for (const k of ['markOn', 'text', 'tag', 'bg']) c[k] = simulateCvd(c[k], o.cvd);
        c.shape = simulateCvd(p.accent, o.cvd);
        c.mark = c.shape;
    }
    return c;
}

function grey(h) {
    const L = hexToOklch(h).L;
    const v = Math.round(Math.max(0, Math.min(1, L)) * 255);
    return '#' + [v, v, v].map((x) => x.toString(16).padStart(2, '0')).join('');
}

/** Второй стоп градиента — тот же тон, сдвинутый по светлоте и оттенку. */
function gradientPartner(h) {
    const c = hexToOklch(h);
    const L = c.L > 0.55 ? Math.max(0.22, c.L - 0.2) : Math.min(0.86, c.L + 0.22);
    return oklchToHex(L, c.C, (c.H + 26) % 360);
}

/* ── сборка сцены ────────────────────────────────────────────────────────── */
let rebuildDone = Promise.resolve();

/**
 * ⚠️ Выдача обязана дождаться сборки. Поля ввода отложены (debounce 220 мс), а
 * контуры букв грузятся с сети — между последним нажатием и готовой сценой есть
 * окно, в котором кнопка «Скачать» отдаёт ПРЕДЫДУЩИЙ логотип. Поймано 12.09.2026
 * на бренд-ките: подпись «обжарка с 2019» была на экране и отсутствовала во всех
 * сорока файлах. Ошибка тихая по построению — человек смотрит на предпросмотр, а
 * проверить может только открыв скачанное.
 */
async function settle() {
    // Добираем то, что человек уже напечатал, но debounce ещё не отдал.
    const fresh = { name: val('lmName'), name2: val('lmName2'), tagline: val('lmTag') };
    let changed = false;
    for (const k of Object.keys(fresh)) {
        if (fresh[k] != null && fresh[k] !== state[k]) { state[k] = fresh[k]; changed = true; }
    }
    if (changed) { historyHold = true; await rebuild(); historyHold = false; }
    await rebuildDone;
}
const val = (id) => { const e = $(id); return e ? e.value : null; };

/** Сборка сцены. Звать через `rebuild()` — он запоминает промис для `settle()`. */
async function doRebuild() {
    const my = ++rebuildSeq;
    faceNote = '';
    const up = (s) => (state.upper ? String(s || '').toUpperCase() : s);
    const name = up(state.name);
    const name2 = up(state.name2);
    const tag = up(state.tagline);
    // Монограмма нужна ВСЕГДА: даже когда в логотипе стоит знак, значок без
    // знака собирать не из чего, кроме букв.
    const mono = monogramOf(name || state.name);
    const [gName, gName2, gTag, gMono] = await Promise.all([
        glyphsFor(name, state.face, state.bold, state.track),
        glyphsFor(name2, state.face, state.bold, state.track),
        glyphsFor(tag, state.face, false, state.track),
        mono ? glyphsFor(mono, state.face, true, 0) : Promise.resolve(null),
    ]);
    // ⚠️ Гонка: пока грузился шрифт, человек успел напечатать ещё букву и
    // запустить следующую сборку. Без этой проверки на экран сядет СТАРАЯ
    // сцена, потому что она пришла позже.
    if (my !== rebuildSeq) return;

    const glyphs = { name: gName, name2: gName2, tag: gTag, mono: gMono };
    const st = Object.assign({}, state, { art });
    scene = buildScene(st, glyphs);
    iconScene = buildScene(st, glyphs, { icon: true });
    await ensureImages();
    stage('editing', { mark: state.mark, layout: state.layout });
    paint();
    saveHash();
    pushHistory();
}

/* Публикуем ИМЕННО последнюю сборку: `settle()` ждёт её, а не первую попавшуюся. */
function rebuild() {
    rebuildDone = doRebuild();
    return rebuildDone;
}

/** Растровую картинку холст рисует только загруженной — ждём её один раз. */
async function ensureImages() {
    const need = new Set();
    for (const s of [scene, iconScene]) {
        if (!s) continue;
        for (const p of s.parts) if (p.kind === 'image') need.add(p.href);
    }
    await Promise.all([...need].filter((h) => !images[h]).map((h) => new Promise((res) => {
        const img = new Image();
        img.onload = () => { images[h] = img; res(); };
        img.onerror = () => res();
        img.src = h;
    })));
}

function paint() {
    if (!scene) return;
    // ⚠️ Версия (цвет / чёрный / белый / серый) красит и ПРЕДПРОСМОТР. Орган,
    // чьё действие видно только в скачанном файле, — не орган: человек выбирает
    // вслепую и узнаёт о выборе, уже открыв файл.
    const vo = variantOpts(state.variant || 'color');
    const c = colors(vo);
    const bg = vo.flat ? (state.variant === 'white' ? '#111827' : '#ffffff')
        : (state.transparent ? null : c.bg);
    // Холст повторяет пропорции логотипа: квадрат вокруг горизонтальной связки
    // — это поля, в которых нечего смотреть.
    const host = $('lmStageBox');
    const W = Math.max(240, Math.min(760, (host ? host.clientWidth : 560) - 24));
    const H = Math.max(160, Math.min(330, Math.round(W * scene.h / scene.w) + 54));
    drawInto($('lmCanvas'), W, { bg, colors: c, pad: 0.06, h: H, scene });

    // ⚠️ Значок — не уменьшенный логотип, а знак: в 16 px горизонтальная
    // связка превращается в полосу. Здесь показан тот же квадрат, который
    // уедет в фавикон.
    //
    // ⚠️ Ряд проверок всегда показывает ЦВЕТНУЮ версию, даже когда наверху
    // выбрана одноцветная: это диагностика ЗАМЫСЛА, а не предпросмотр файла.
    // Иначе выбор «чёрный» красит все шесть квадратов чёрным, и проверка «а
    // виден ли знак в одну краску» отвечает сама себе.
    const base = colors();
    const bg0 = state.transparent ? null : base.bg;
    drawInto($('lmS16'), 16, { bg: bg0 || '#ffffff', colors: base, pad: 0.06, scene: iconScene });
    drawInto($('lmS32'), 32, { bg: bg0 || '#ffffff', colors: base, pad: 0.06, scene: iconScene });
    drawInto($('lmS64'), 48, { bg: bg0 || '#ffffff', colors: base, pad: 0.1, bgShape: 'circle', scene: iconScene });
    drawInto($('lmSDark'), 48, { bg: DARK_BG, colors: base, pad: 0.1, bgShape: 'circle', scene: iconScene });
    drawInto($('lmSFlat'), 48, { bg: '#ffffff', colors: colors({ flat: '#000000' }), pad: 0.12, scene: iconScene });
    drawInto($('lmSCvd'), 48, { bg: simulateCvd(bg0 || '#ffffff', 'deut'), colors: colors({ cvd: 'deut' }), pad: 0.1, bgShape: 'circle', scene: iconScene });

    renderVerdict(base);
    const fn = $('lmFaceNote');
    if (fn) { fn.textContent = faceNote; fn.hidden = !faceNote; }
    const rn = $('lmRasterNote');
    if (rn) rn.hidden = !sceneHasRaster(scene);
}

/**
 * Вердикт — три числа, каждое про свой способ отказать. Контраст ловит «серое
 * на белом», дальтонизм — «красное на зелёном» (там контраст бывает приличный,
 * а разницы не видно), одна краска — печать, факс, тиснение и штамп.
 */
const DARK_BG = '#0b1220';

function renderVerdict(c) {
    const host = $('lmVerdict');
    if (!host) return;
    const bg = state.transparent ? '#ffffff' : c.bg;
    const markC = typeof c.mark === 'object' ? c.mark.g[0] : c.mark;
    const cr = contrast(markC, bg);
    const cv = cvdSafe(markC, bg);
    host.innerHTML = '';
    host.appendChild(chipEl(t('check_contrast'), cr.toFixed(1), cr >= 4.5 ? 'ok' : cr >= 3 ? 'warn' : 'bad'));
    host.appendChild(chipEl(t('check_cvd'), cv.toFixed(1), cv >= 3 ? 'ok' : cv >= 2 ? 'warn' : 'bad'));
    // ⚠️ Тёмный фон меряется ОТДЕЛЬНО и обязан быть здесь: без него чёрный знак
    // на чёрном кружке выглядит просто чёрным кружком, и человек читает это как
    // поломку рисовалки, а не как вывод «на тёмном логотип исчезает». Проверка,
    // которая молчит о найденном, ничем не лучше отсутствующей.
    const dk = contrast(markC, DARK_BG);
    host.appendChild(chipEl(t('check_dark'), dk.toFixed(1), dk >= 4.5 ? 'ok' : dk >= 3 ? 'warn' : 'bad'));
    const tiny = state.tagline && state.mark !== 'none';
    host.appendChild(chipEl(t('check_small'), tiny ? t('check_small_tag') : t('check_small_ok'), tiny ? 'warn' : 'ok'));
}

function chipEl(label, value, tone) {
    const b = document.createElement('span');
    b.className = 'lm-vd lm-' + tone;
    b.innerHTML = '<i></i><b></b><u></u>';
    b.querySelector('b').textContent = label;
    b.querySelector('u').textContent = value;
    return b;
}

function drawInto(cv, size, o) {
    if (!cv) return;
    const h = o.h || size;
    // ⚠️ Мелкие предпросмотры рисовались в один пиксель на точку, и на экране
    // телефона (3 точки на пиксель) выходило мыло — тул выглядел так, будто
    // это он плохо рисует. Урок этого ряда про РАЗМЕР, а не про число пикселей:
    // настоящий фавикон на retina берётся из крупного битмапа и выглядит
    // резким, поэтому и здесь рисуем по плотности экрана.
    const dpr = Math.min(size > 100 ? 2 : 3, window.devicePixelRatio || 1);
    cv.width = Math.round(size * dpr);
    cv.height = Math.round(h * dpr);
    cv.style.width = size + 'px';
    cv.style.height = h + 'px';
    const g = cv.getContext('2d');
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawScene(g, o.scene || scene, Object.assign({ size, w: size, h, images }, o));
}

/* ── адресная строка ─────────────────────────────────────────────────────── */
/**
 * ⚠️ Своя картинка в ссылку НЕ кладётся: она весит килобайты и уехала бы в
 * адресную строку и в историю браузера. Ссылка несёт настройки — этого хватает,
 * чтобы продолжить с того же места на другой машине и отправить вариант.
 */
const HASH_KEYS = { n: 'name', n2: 'name2', t: 'tagline', f: 'face', m: 'mark',
    s: 'shape', l: 'layout', p: 'palette', c: 'brand', md: 'mode', al: 'align' };

function saveHash() {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(HASH_KEYS)) if (state[v]) q.set(k, state[v]);
    if (state.upper) q.set('u', '1');
    if (!state.bold) q.set('b', '0');
    if (state.track) q.set('tr', String(state.track));
    if (state.markScale !== 1) q.set('ms', String(state.markScale));
    if (state.gap !== DEFAULTS.gap) q.set('gp', String(state.gap));
    if (state.gradient) q.set('gr', '1');
    if (state.transparent) q.set('tp', '1');
    try { history.replaceState(null, '', '#' + q.toString()); } catch (_) { /* песочница */ }
}

function loadHash() {
    const h = String(location.hash || '').replace(/^#/, '');
    if (!h) return false;
    const q = new URLSearchParams(h);
    if (!q.get('n')) return false;
    for (const [k, v] of Object.entries(HASH_KEYS)) if (q.get(k) != null) state[v] = q.get(k);
    state.upper = q.get('u') === '1';
    state.bold = q.get('b') !== '0';
    state.track = numOr(q.get('tr'), 0);
    state.markScale = numOr(q.get('ms'), 1);
    state.gap = numOr(q.get('gp'), DEFAULTS.gap);
    state.gradient = q.get('gr') === '1';
    state.transparent = q.get('tp') === '1';
    if (state.mark === 'custom') state.mark = 'ring';   // картинки в ссылке нет
    return true;
}
const numOr = (v, d) => { const f = parseFloat(v); return isFinite(f) ? f : d; };

/* ── выдача ──────────────────────────────────────────────────────────────── */
/** Логотип выгружается в своих пропорциях, значок — квадратом. */
function frame(size, sc) {
    const s = sc || scene;
    if (s.icon) return { w: size, h: size };
    const h = Math.round(size * s.h / s.w);
    return { w: size, h: Math.max(1, Math.min(size, h)) };
}

function svgText(size, opts) {
    const o = opts || {};
    const c = colors(o);
    const sc = o.scene || scene;
    const f = o.w ? { w: o.w, h: o.h } : frame(size || 512, sc);
    return sceneToSvg(sc, Object.assign({
        w: f.w, h: f.h,
        bg: o.bgOverride !== undefined ? o.bgOverride : (state.transparent || o.flat ? null : c.bg),
        colors: c,
        pad: 0.08,
        title: state.name,
    }, o));
}

async function pngBlob(size, opts) {
    const o = opts || {};
    const c = colors(o);
    const sc = o.scene || scene;
    const f = o.w ? { w: o.w, h: o.h } : frame(size, sc);
    const cv = document.createElement('canvas');
    cv.width = f.w;
    cv.height = f.h;
    const g = cv.getContext('2d');
    drawScene(g, sc, Object.assign({
        w: f.w, h: f.h,
        bg: o.bgOverride !== undefined ? o.bgOverride : (state.transparent || o.flat ? null : c.bg),
        colors: c, pad: 0.08, images,
    }, o));
    return new Promise((r) => cv.toBlob(r, 'image/png'));
}

function baseName() {
    const raw = (state.name || 'logo').trim() || 'logo';
    try {
        if (window.__outputName) return window.__outputName.sanitize(raw.slice(0, 40));
    } catch (_) { /* примитив не загрузился */ }
    return 'logo';
}
const join = (base, ext) => {
    try {
        if (window.__outputName) return window.__outputName.join(base, ext);
    } catch (_) { /* примитив не загрузился */ }
    return base + '.' + ext;
};

function variantOpts(id) {
    const v = KIT.VARIANTS.find((x) => x.id === id) || KIT.VARIANTS[0];
    const o = {};
    if (v.flat) { o.flat = v.flat; o.bgOverride = null; }
    if (v.grey) o.grey = true;
    return o;
}

async function saveSvg() {
    await settle();
    const blob = new Blob([svgText(512, variantOpts(state.variant || 'color'))], { type: 'image/svg+xml' });
    window.__downloadFile(blob, join(baseName(), 'svg'));
    ae('export', { f: 'svg', mark: state.mark, shape: state.shape, layout: state.layout,
        palette: state.brand ? 'custom' : state.palette, art: art ? art.kind : 0, tag: state.tagline ? 1 : 0 });
    try { if (window.__aeToolComplete) window.__aeToolComplete({ via: 'svg', mark: state.mark }); } catch (_) {}
}

async function savePng() {
    await settle();
    const blob = await pngBlob(state.size, variantOpts(state.variant || 'color'));
    window.__downloadFile(blob, join(baseName() + '-' + state.size, 'png'));
    ae('export', { f: 'png', size: state.size, mark: state.mark, transparent: state.transparent ? 1 : 0 });
    try { if (window.__aeToolComplete) window.__aeToolComplete({ via: 'png', size: state.size }); } catch (_) {}
}

/**
 * Набор значков — то, ради чего логотип чаще всего и делают. Пачка идёт через
 * общий примитив: браузер отдаёт ОДИН файл на жест, и цикл загрузок тут убил
 * бы вкладку целиком.
 */
async function saveIcons() {
    await settle();
    await runBatch($('lmIcons'), join(baseName() + '-icons', 'zip'), ICON_COUNT, async (write) => {
        const pngs = [];
        for (const it of ICONS) {
            const blob = await iconPng(it);
            pngs.push({ blob, size: it.s });
            await write(it.n, blob);
        }
        await write('favicon.svg', new Blob([svgText(512, { scene: iconScene })], { type: 'image/svg+xml' }));
        // ICO собирается из УЖЕ посчитанных 16/32/48 — второй раз их не рисуем.
        await write('favicon.ico', await KIT.buildIco(pngs.filter((p) => p.size <= 48)));
        await write('site.webmanifest', txt(KIT.webmanifest(state.name, basePalette().bg, basePalette().accent)));
        await write('head.html', txt(KIT.faviconHtml()));
    });
    ae('export', { f: 'icons', n: ICON_COUNT, mark: state.mark });
    try { if (window.__aeToolComplete) window.__aeToolComplete({ via: 'icons', n: ICON_COUNT }); } catch (_) {}
}

const ICONS = KIT.ICONS;
const ICON_COUNT = ICONS.length + 4;

async function iconPng(it) {
    // ⚠️ Ключ `bg: undefined` НЕ «оставить как есть»: Object.assign копирует и
    // undefined, затирая фон. Поэтому ключа просто нет, когда мы его не задаём.
    const opts = { scene: iconScene, pad: it.pad == null ? 0.1 : it.pad };
    if (it.opaque) opts.bgOverride = state.transparent ? '#ffffff' : basePalette().bg;
    return pngBlob(it.s, opts);
}

const txt = (s) => new Blob([s], { type: 'text/plain;charset=utf-8' });

/**
 * Бренд-кит — то, за что вся отрасль берёт деньги: Wix $49–99, Looka $65–96/год,
 * Tailor Brands обещает «21 размер» и не публикует ни одного. Здесь он собран
 * из ТОЙ ЖЕ сцены, поэтому все версии заведомо совпадают друг с другом, а
 * размеры напечатаны и в интерфейсе, и в README внутри архива.
 */
async function saveKit() {
    await settle();
    if (!isPro()) { offerPro('kit'); return; }
    const n = 2 + KIT.VARIANTS.length * 2 + ICONS.length + 4 + KIT.AVATARS.length + KIT.COVERS.length + 5;
    await runBatch($('lmKit'), join(baseName() + '-brand-kit', 'zip'), n, async (write) => {
        const pal = basePalette();
        const files = paletteFiles(pal, state.name);

        for (const v of KIT.VARIANTS) {
            const o = variantOpts(v.id);
            await write('logo-' + v.dir + '.svg', new Blob([svgText(1024, o)], { type: 'image/svg+xml' }));
            await write('logo-' + v.dir + '-2048.png', await pngBlob(2048, o));
        }
        await write('icon.svg', new Blob([svgText(512, { scene: iconScene })], { type: 'image/svg+xml' }));
        await write('icon-1024.png', await pngBlob(1024, { scene: iconScene }));

        const pngs = [];
        for (const it of ICONS) {
            const blob = await iconPng(it);
            pngs.push({ blob, size: it.s });
            await write(it.n, blob);
        }
        await write('favicon.ico', await KIT.buildIco(pngs.filter((p) => p.size <= 48)));
        await write('favicon.svg', new Blob([svgText(512, { scene: iconScene })], { type: 'image/svg+xml' }));
        await write('site.webmanifest', txt(KIT.webmanifest(state.name, pal.bg, pal.accent)));
        await write('head.html', txt(KIT.faviconHtml()));

        for (const a of KIT.AVATARS) {
            await write(a.n, await pngBlob(a.s, { scene: iconScene, pad: 0.14, bgOverride: pal.bg }));
        }
        for (const cv of KIT.COVERS) {
            await write(cv.n, await pngBlob(0, { w: cv.w, h: cv.h, pad: 0.2, bgOverride: pal.bg }));
        }

        await write('palette.css', txt(files.css));
        await write('palette.json', txt(files.json));
        await write('tailwind.colors.js', txt(files.tailwind));
        await write('brand-sheet.svg', new Blob([sheetSvg()], { type: 'image/svg+xml' }));
        await write('README.txt', txt(KIT.readme({
            name: state.name, colors: pal, face: labelOf(state.face),
            domain: location.host, t: I18N,
        })));
    });
    ae('export', { f: 'kit', n });
    try { if (window.__aeToolComplete) window.__aeToolComplete({ via: 'kit' }); } catch (_) {}
}

function sheetSvg() {
    const inner = (w, h, o) => {
        const s = svgText(0, Object.assign({ w, h, pad: 0.12, bgOverride: null }, o || {}));
        return s.replace(/^<svg[^>]*>/, '').replace(/<\/svg>$/, '');
    };
    return KIT.brandSheetSvg({
        name: state.name,
        colors: basePalette(),
        face: labelOf(state.face),
        domain: location.host,
        t: I18N,
        logo: (w, h, o) => inner(w, h, o),
        icon: (w, h) => inner(w, h, { scene: iconScene }),
    });
}

/** Общая обвязка пачки: кнопка, прогресс, отказ по-человечески. */
async function runBatch(btn, zipName, total, fn) {
    if (btn) { btn.disabled = true; btn.classList.add('lm-busy'); }
    const note = $('lmBatchNote');
    let done = 0;
    try {
        const batch = await window.__batchDeliver.openBatch({
            slug: SLUG, zipName, total,
            onProgress: (d, tt) => { if (note) note.textContent = t('batch_progress').replace(':d', d).replace(':t', tt); },
        });
        await fn(async (name, blob) => {
            await batch.write(name, blob);
            done++;
            if (note) note.textContent = t('batch_progress').replace(':d', done).replace(':t', total);
        });
        await batch.finish();
        if (note) note.textContent = t('batch_done').replace(':n', done);
    } catch (e) {
        if (note) note.textContent = '';
        window.__handleError(e, SLUG);
    } finally {
        if (btn) { btn.disabled = false; btn.classList.remove('lm-busy'); }
    }
}

/* ── Премиум ─────────────────────────────────────────────────────────────── */
/**
 * ⚠️ Признак — `is_pro`, поле, которое layout действительно публикует. Чтение
 * несуществующего `plan` было бы всегда ложным, то есть ПЛАТЯЩИЙ человек молча
 * получал бы отказ — отказ в единственную сторону, о которой не сообщают.
 */
function isPro() {
    try { if (window.__tierLimits && typeof window.__tierLimits.is_pro === 'boolean') return window.__tierLimits.is_pro; } catch (_) {}
    try { return !!(document.body && document.body.dataset && document.body.dataset.pro === '1'); } catch (_) {}
    return false;
}

function offerPro(what) {
    ae('pro_offer', { what });
    const box = $('lmProBox');
    if (box) {
        box.hidden = false;
        box.scrollIntoView({ block: 'nearest' });
    }
    try { if (window.__toast) window.__toast(t('pro_needed'), 'info'); } catch (_) {}
}

function syncPro() {
    const pro = isPro();
    document.querySelectorAll('[data-lm-pro]').forEach((el) => el.classList.toggle('lm-locked', !pro));
    const box = $('lmProBox');
    if (box && pro) box.hidden = true;
}

/* ── варианты ────────────────────────────────────────────────────────────── */
/**
 * ⚠️ Это самый сильный приём категории, и он про ВКУС, а не про генерацию.
 * Человек, который заводит первое дело, не может назвать шрифт, но безошибочно
 * тычет в то, что ему нравится. Looka учит вкусу стеной образцов, Tailor Brands
 * — парами «это или то». Здесь дешевле и честнее: восемь готовых сочетаний из
 * НАШИХ же знаков, палитр и шрифтов, и любое можно доработать дальше.
 */
const VAR_COUNT = 8;
let variants = [];

/**
 * Знаки, ОТНОСЯЩИЕСЯ к названию. «Тихий Дом» → дом, «мастерская света» → лампа.
 *
 * ⚠️ Разбираем и название, и подпись, потому что род занятий чаще стоит именно
 * в подписи: имя бренда — это имя, а «мастерская света» — это уже отрасль.
 *
 * ⚠️ Порог — ТРИ буквы, а не четыре. На четырёх «Тихий Дом» терял ровно то
 * слово, ради которого подсказка и делалась (замер 12.09.2026: дом, лист, код,
 * цель, рука — всё это трёхбуквенные ключи наших знаков). Служебные «для», «или»,
 * «над» при этом ничего не ломают: их нет ни в одном списке ключевых слов, а
 * ищем мы ПО НИМ, а не среди них.
 */
function relatedMarks() {
    const words = (state.name + ' ' + state.tagline)
        .toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((w) => w.length >= 3);
    const hit = new Set();
    for (const w of words) {
        for (const m of searchMarks(w, 'all')) hit.add(m.id);
    }
    return [...hit];
}

function rollVariants(keepFirst) {
    const all = MARKS.map((m) => m.id);
    // Половина подсказок — по смыслу названия, половина — наугад: только
    // относящиеся дают восемь вариаций одного и того же, только случайные —
    // ощущение, что инструмент не читал, что ты написал.
    const related = relatedMarks();
    const fc = faces().map((f) => f.id);
    const out = keepFirst ? [pick(variants) || snapVariant()] : [];
    const wantRelated = Math.min(related.length, Math.ceil(VAR_COUNT / 2));
    const bag = shuffle(related).slice(0, wantRelated);
    // ⚠️ Знаки в ряду не повторяются: две одинаковые плитки рядом читаются как
    // поломка, а не как «нам нравится этот знак».
    const used = new Set(out.map((v) => v.mark));
    const nextMark = () => {
        while (bag.length) { const m = bag.pop(); if (!used.has(m)) return m; }
        for (let i = 0; i < 40; i++) { const m = rnd(all); if (!used.has(m)) return m; }
        return rnd(all);
    };
    while (out.length < VAR_COUNT) {
        const mk = nextMark();
        used.add(mk);
        out.push({
            mark: mk,
            shape: rnd(['none', 'none', 'circle', 'squircle', 'square']),
            layout: rnd(LAYOUTS),
            palette: rnd(PALETTES).id,
            face: rnd(fc),
            upper: Math.random() < 0.35,
            track: rnd([0, 0, 0.02, 0.06]),
        });
    }
    variants = out;
    renderVariants();
    ae('ideas', { related: related.length });
}

function shuffle(a) {
    const x = a.slice();
    for (let i = x.length - 1; i > 0; i--) {
        const j = (Math.random() * (i + 1)) | 0;
        [x[i], x[j]] = [x[j], x[i]];
    }
    return x;
}
const rnd = (a) => a[(Math.random() * a.length) | 0];
const pick = (a) => (a && a.length ? a[0] : null);
function snapVariant() {
    return { mark: state.mark, shape: state.shape, layout: state.layout,
        palette: state.palette, face: state.face, upper: state.upper, track: state.track };
}

function renderVariants() {
    const host = $('lmVariants');
    if (!host) return;
    host.innerHTML = '';
    variants.forEach((v, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'lm-var';
        // ⚠️ Подпись для читалки — переведённая. Здесь стоял идентификатор знака
        // (`ring`, `cup`), то есть английское служебное слово, зачитываемое
        // русскому человеку: снаружи не видно, а услышать можно только это.
        b.setAttribute('aria-label', t('idea_n').replace(':n', String(i + 1)));
        b.dataset.mark = v.mark;
        const cv = document.createElement('canvas');
        b.appendChild(cv);
        b.addEventListener('click', () => {
            Object.assign(state, v);
            state.brand = '';
            syncInputs();
            rebuild();
            ae('variant_pick', { mark: v.mark, palette: v.palette });
        });
        host.appendChild(b);
        // Рисуем ПОСЛЕ вставки: до неё у канвы нет размеров, и битмап 0×0
        // молча не рисует ничего (та же ловушка, что у скрытых панелей).
        drawVariant(cv, v);
    });
}

/**
 * ⚠️ Плитка рисуется СИНХРОННО и шрифта не ждёт. Здесь стоял
 * `await glyphsFor(mono, v.face, …)` — загрузка ШРИФТА ради контуров, которые
 * эта плитка не использует вовсе: `rollVariants` кладёт в `mark` только наши
 * знаки, никогда 'monogram', поэтому `iconScene` рисует путь знака и глифы
 * выбрасывает. Цена ошибки видна только на ХОЛОДНОЙ загрузке: восемь случайных
 * гарнитур — это восемь скачиваний TTF, и до их прихода панель «Идеи» стоит
 * восемью пустыми белыми коробками. На прогретом кэше всё рисуется мгновенно,
 * поэтому при разработке дефекта не видно совсем (замечен на первом же заходе
 * на прод 12.09.2026).
 */
function drawVariant(cv, v) {
    try {
        const p = PALETTES.find((x) => x.id === v.palette) || PALETTES[0];
        const sc = buildScene({ mark: v.mark, shape: v.shape, layout: v.layout, markScale: 1, gap: 0.26 },
            {}, { icon: true });
        const S = 52;
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        cv.width = S * dpr;
        cv.height = S * dpr;
        cv.style.width = S + 'px';
        cv.style.height = S + 'px';
        const ctx = cv.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        drawScene(ctx, sc, { w: S, h: S, pad: 0.1, bg: p.bg, bgShape: 'round',
            colors: { shape: p.accent, markOn: p.on, mark: p.accent, text: p.ink, tag: p.tag, bg: p.bg } });
    } catch (_) { /* один вариант не нарисовался — остальные важнее */ }
}

/* ── своя картинка ───────────────────────────────────────────────────────── */
async function onArtFile(file) {
    if (!file) return;
    const note = $('lmArtNote');
    const set = (msg, bad) => { if (note) { note.textContent = msg; note.className = 'tc-hint' + (bad ? ' lm-bad' : ''); } };
    set(t('art_working'));
    try {
        const isSvg = /svg/i.test(file.type) || /\.svg$/i.test(file.name || '');
        if (isSvg) {
            art = await parseSvg(await fileToText(file));
            set(art.recolor ? t('art_svg_mono') : t('art_svg_color'));
        } else {
            // Растр: сперва пробуем обвести — так он становится настоящим
            // вектором и начинает вести себя как знак. Не вышло — кладём как
            // есть и ГОВОРИМ об этом: обещание «настоящий вектор» иначе врёт.
            try {
                art = await traceRaster(file, { detail: numOr($('lmArtDetail') && $('lmArtDetail').value, 0.6) });
                set(t('art_traced').replace(':n', art.loops));
            } catch (_) {
                art = await rasterArt(file);
                set(t('art_raster'), true);
            }
        }
        artName = file.name || 'art';
        state.mark = 'custom';
        syncArtUi();
        markChips();
        await rebuild();
        ae('art_add', { kind: art.kind, traced: art.traced ? 1 : 0, recolor: art.recolor ? 1 : 0 });
    } catch (e) {
        set(t('art_failed'), true);
        ae('art_fail', { m: String((e && e.message) || e).slice(0, 40) });
    }
}

function clearArt() {
    art = null;
    artName = '';
    if (state.mark === 'custom') state.mark = 'ring';
    const note = $('lmArtNote');
    if (note) note.textContent = '';
    syncArtUi();
    markChips();
    rebuild();
}

function syncArtUi() {
    const row = $('lmArtRow');
    if (row) row.hidden = !art;
    const nm = $('lmArtName');
    if (nm) nm.textContent = artName;
    const re = $('lmArtRetrace');
    if (re) re.hidden = !(art && art.traced);
    const inv = $('lmArtInvert');
    if (inv) inv.hidden = !(art && art.traced);
}

/* ── телеметрия ──────────────────────────────────────────────────────────── */
/* Воронка: дошёл ли человек от имени до файла, и каким письмом набрана
   надпись (подмена шрифта = какое письмо мы не покрываем декоративными). */
const staged = {};
function stage(name, meta) {
    if (staged[name]) return;
    staged[name] = 1;
    try { if (window.__stage) window.__stage(name, meta || {}); } catch (_) { /* не мешаем работе */ }
}
const ae = (action, p) => {
    try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: SLUG, action }, p || {})); } catch (_) { /* телеметрия не мешает работе */ }
};

/* ── интерфейс ───────────────────────────────────────────────────────────── */

function tabs() {
    const bar = $('lmTabs');
    if (!bar) return;
    bar.querySelectorAll('[data-lm-tab]').forEach((b) => {
        b.addEventListener('click', () => {
            const id = b.getAttribute('data-lm-tab');
            bar.querySelectorAll('[data-lm-tab]').forEach((x) => {
                const on = x === b;
                x.classList.toggle('on', on);
                x.setAttribute('aria-selected', on ? 'true' : 'false');
            });
            document.querySelectorAll('[data-lm-panel]').forEach((p) => {
                p.hidden = p.getAttribute('data-lm-panel') !== id;
            });
            ae('tab', { id });
        });
    });
}

/** Список шрифтов: каждое название набрано СВОИМ шрифтом. */
function fontMenu() {
    const host = $('lmFaceList');
    if (!host) return;
    host.innerHTML = '';
    for (const f of faces()) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'tc-opt lm-face';
        b.setAttribute('data-id', f.id);
        b.setAttribute('aria-selected', f.id === state.face ? 'true' : 'false');
        b.textContent = f.label;
        try {
            const fam = window.PdfTextFont.cssFamily && window.PdfTextFont.cssFamily(f.id, false);
            if (fam) b.style.fontFamily = '"' + fam + '", system-ui, sans-serif';
        } catch (_) { /* подпись останется системным шрифтом — не беда */ }
        b.addEventListener('click', () => {
            state.face = f.id;
            closeMenu();
            syncFaceButton();
            rebuild();
            ae('face', { id: f.id });
        });
        host.appendChild(b);
    }
}

function syncFaceButton() {
    const b = $('lmFaceBtn');
    if (!b) return;
    b.querySelector('span').textContent = labelOf(state.face);
    try {
        const fam = window.PdfTextFont.cssFamily && window.PdfTextFont.cssFamily(state.face, false);
        b.querySelector('span').style.fontFamily = fam ? '"' + fam + '", system-ui, sans-serif' : '';
    } catch (_) {}
    $('lmFaceList').querySelectorAll('[data-id]').forEach((x) =>
        x.setAttribute('aria-selected', x.getAttribute('data-id') === state.face ? 'true' : 'false'));
}

function closeMenu() {
    const m = $('lmFaceList');
    if (m) m.hidden = true;
    const b = $('lmFaceBtn');
    if (b) b.setAttribute('aria-expanded', 'false');
}

/** Сетка знаков с поиском и рубриками. */
function markChips() {
    const host = $('lmMarks');
    if (!host) return;
    const q = ($('lmMarkSearch') && $('lmMarkSearch').value) || '';
    const cat = host.getAttribute('data-cat') || 'all';
    const list = searchMarks(q, cat);
    host.innerHTML = '';

    const add = (id, label, svg) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'lm-mk' + (id === state.mark ? ' on' : '');
        b.setAttribute('data-id', id);
        b.title = label || id;
        b.setAttribute('aria-label', label || id);
        b.innerHTML = svg || '<em>' + label + '</em>';
        b.addEventListener('click', () => {
            state.mark = id;
            markChips();
            rebuild();
            ae('mark', { id });
        });
        host.appendChild(b);
    };

    if (!q) {
        add('none', t('mark_none'), '<svg viewBox="0 0 100 100" width="22" height="22" aria-hidden="true"><path d="M14 46h72v8H14z" fill="currentColor"/></svg>');
        add('monogram', t('mark_monogram'), '<b class="lm-mono">' + (monogramOf(state.name) || 'AB') + '</b>');
        if (art) add('custom', t('mark_custom'), '<svg viewBox="0 0 100 100" width="22" height="22" aria-hidden="true"><path d="M12 20h76v60H12zM24 66l18-22 14 17 10-11 12 16z" fill="currentColor"/></svg>');
    }
    for (const m of list) {
        add(m.id, m.id, '<svg viewBox="0 0 100 100" width="22" height="22" aria-hidden="true">'
            + '<path d="' + m.path + '"' + (m.rule ? ' fill-rule="evenodd"' : '') + ' fill="currentColor"/></svg>');
    }
    const empty = $('lmMarkEmpty');
    if (empty) empty.hidden = list.length > 0;
    const cnt = $('lmMarkCount');
    if (cnt) cnt.textContent = String(list.length);
}

function markCats() {
    const host = $('lmMarkCats');
    if (!host) return;
    host.innerHTML = '';
    const mk = (id, label) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'tc-chip' + (id === 'all' ? ' on' : '');
        b.textContent = label;
        b.addEventListener('click', () => {
            $('lmMarks').setAttribute('data-cat', id);
            host.querySelectorAll('.tc-chip').forEach((x) => x.classList.toggle('on', x === b));
            markChips();
        });
        host.appendChild(b);
    };
    mk('all', t('mark_cat_all'));
    for (const c of MARK_CATS) mk(c, t('mark_cat_' + c, c));
}

function paletteChips() {
    const host = $('lmPalettes');
    if (!host) return;
    host.innerHTML = '';
    for (const p of PALETTES) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'lm-pal' + (!state.brand && p.id === state.palette ? ' on' : '');
        b.setAttribute('data-id', p.id);
        b.setAttribute('aria-label', p.id);
        b.innerHTML = '<i style="background:' + p.bg + '"></i><i style="background:' + p.accent + '"></i><i style="background:' + p.ink + '"></i>';
        b.addEventListener('click', () => {
            state.palette = p.id;
            state.brand = '';
            syncColorUi();
            paletteChips();
            paint();
            saveHash();
            pushHistory();
            ae('palette', { id: p.id });
        });
        host.appendChild(b);
    }
}

/**
 * Пипетка экрана — свежая возможность браузера (EyeDropper), и она тут по делу:
 * фирменный цвет чаще всего УЖЕ где-то есть — на сайте, в макете, в фотографии
 * вывески. Где её нет, остаётся обычное поле цвета: та же работа, на один шаг
 * длиннее.
 */
function setupEyedropper() {
    const b = $('lmPick');
    if (!b) return;
    if (typeof window.EyeDropper !== 'function') { b.hidden = true; return; }
    b.addEventListener('click', async () => {
        try {
            const r = await new window.EyeDropper().open();
            if (r && r.sRGBHex) applyBrand(r.sRGBHex);
            ae('eyedropper');
        } catch (_) { /* человек передумал — это не ошибка */ }
    });
}

function applyBrand(hex) {
    state.brand = hex;
    const inp = $('lmBrand');
    if (inp) inp.value = hex;
    syncColorUi();
    paletteChips();
    paint();
    saveHash();
    pushHistory();
    ae('brand_color', { mode: state.mode });
}

function syncColorUi() {
    const on = !!state.brand;
    const box = $('lmBrandOut');
    if (box) {
        box.hidden = !on;
        if (on) {
            const p = basePalette();
            box.innerHTML = '';
            for (const [k, v] of [['accent', p.accent], ['ink', p.ink], ['tag', p.tag], ['bg', p.bg]]) {
                const s = document.createElement('span');
                s.className = 'lm-sw';
                s.style.background = v;
                s.title = k + ' ' + v.toUpperCase();
                box.appendChild(s);
            }
        }
    }
    const clr = $('lmBrandClear');
    if (clr) clr.hidden = !on;
}

function sliders() {
    const bind = (id, key, fmt) => {
        const el = $(id);
        if (!el) return;
        const out = $(id + 'Out');
        const show = () => { if (out) out.textContent = fmt ? fmt(state[key]) : String(state[key]); };
        el.value = String(state[key]);
        show();
        el.addEventListener('input', () => {
            state[key] = parseFloat(el.value);
            show();
            rebuildSoon();
        });
        el.addEventListener('change', () => { pushHistory(); ae('slider', { k: key }); });
    };
    bind('lmTrack', 'track', (v) => (v > 0 ? '+' : '') + Math.round(v * 1000) / 10);
    bind('lmScale', 'markScale', (v) => Math.round(v * 100) + '%');
    bind('lmGap', 'gap', (v) => Math.round(v * 100) + '%');
}

let soonId = 0;
function rebuildSoon() {
    clearTimeout(soonId);
    soonId = setTimeout(() => { historyHold = true; rebuild().then(() => { historyHold = false; }); }, 90);
}

function segments() {
    document.querySelectorAll('[data-lm-seg]').forEach((host) => {
        const key = host.getAttribute('data-lm-seg');
        host.querySelectorAll('[data-v]').forEach((b) => {
            b.addEventListener('click', () => {
                state[key] = b.getAttribute('data-v');
                host.querySelectorAll('[data-v]').forEach((x) => x.classList.toggle('on', x === b));
                rebuild();
                ae('seg', { k: key, v: state[key] });
            });
        });
    });
}

function syncInputs() {
    const set = (id, v) => { const e = $(id); if (e) e.value = v; };
    const chk = (id, v) => { const e = $(id); if (e) e.checked = !!v; };
    set('lmName', state.name);
    set('lmName2', state.name2);
    set('lmTag', state.tagline);
    set('lmBrand', state.brand || '#3b82f6');
    chk('lmBold', state.bold);
    chk('lmUpper', state.upper);
    chk('lmGrad', state.gradient);
    chk('lmTransparent', state.transparent);
    set('lmTrack', state.track);
    set('lmScale', state.markScale);
    set('lmGap', state.gap);
    document.querySelectorAll('[data-lm-seg]').forEach((host) => {
        const key = host.getAttribute('data-lm-seg');
        host.querySelectorAll('[data-v]').forEach((x) =>
            x.classList.toggle('on', x.getAttribute('data-v') === String(state[key])));
    });
    const out = (id, v) => { const e = $(id); if (e) e.textContent = v; };
    out('lmTrackOut', (state.track > 0 ? '+' : '') + Math.round(state.track * 1000) / 10);
    out('lmScaleOut', Math.round(state.markScale * 100) + '%');
    out('lmGapOut', Math.round(state.gap * 100) + '%');
    syncFaceButton();
    syncColorUi();
    syncArtUi();
    paletteChips();
    markChips();
}

function bind() {
    const deb = (fn, ms) => { let id = 0; return () => { clearTimeout(id); id = setTimeout(fn, ms); }; };
    const text = (id, key) => {
        const el = $(id);
        if (!el) return;
        el.addEventListener('input', deb(() => {
            state[key] = el.value;
            historyHold = true;
            rebuild().then(() => { historyHold = false; });
        }, 220));
        el.addEventListener('change', () => pushHistory());
    };
    text('lmName', 'name');
    text('lmName2', 'name2');
    text('lmTag', 'tagline');

    const flag = (id, key, repaintOnly) => {
        const el = $(id);
        if (!el) return;
        el.addEventListener('change', () => {
            state[key] = el.checked;
            if (repaintOnly) { paint(); saveHash(); pushHistory(); } else rebuild();
            ae('flag', { k: key, v: el.checked ? 1 : 0 });
        });
    };
    flag('lmBold', 'bold');
    flag('lmUpper', 'upper');
    flag('lmGrad', 'gradient', true);
    flag('lmTransparent', 'transparent', true);

    const fb = $('lmFaceBtn');
    if (fb) {
        fb.addEventListener('click', () => {
            const m = $('lmFaceList');
            const open = m.hidden;
            m.hidden = !open;
            fb.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
        document.addEventListener('click', (e) => {
            if (!fb.contains(e.target) && !$('lmFaceList').contains(e.target)) closeMenu();
        });
    }

    const ms = $('lmMarkSearch');
    if (ms) ms.addEventListener('input', deb(() => { markChips(); ae('mark_search'); }, 140));

    const brand = $('lmBrand');
    if (brand) brand.addEventListener('input', deb(() => applyBrand(brand.value), 120));
    const bc = $('lmBrandClear');
    if (bc) bc.addEventListener('click', () => {
        state.brand = '';
        syncColorUi();
        paletteChips();
        paint();
        saveHash();
        pushHistory();
    });

    const size = $('lmSize');
    if (size) size.addEventListener('change', () => { state.size = parseInt(size.value, 10) || 1024; });

    document.querySelectorAll('[data-lm-var]').forEach((b) => {
        b.addEventListener('click', () => {
            state.variant = b.getAttribute('data-lm-var');
            document.querySelectorAll('[data-lm-var]').forEach((x) => x.classList.toggle('on', x === b));
            paint();
            ae('variant', { v: state.variant });
        });
    });

    on('lmSvg', saveSvg);
    on('lmPng', savePng);
    on('lmIcons', saveIcons);
    on('lmKit', saveKit);
    on('lmUndo', undo);
    on('lmRedo', redo);
    on('lmRoll', () => { rollVariants(false); ae('variants_roll'); });
    on('lmArtClear', clearArt);
    on('lmArtInvert', async () => {
        if (!art || !art.traced) return;
        const f = lastArtFile;
        if (!f) return;
        art = await traceRaster(f, { invert: !lastInvert, detail: numOr($('lmArtDetail').value, 0.6) });
        lastInvert = !lastInvert;
        rebuild();
    });
    on('lmArtRetrace', async () => {
        if (!lastArtFile) return;
        art = await traceRaster(lastArtFile, { invert: lastInvert, detail: numOr($('lmArtDetail').value, 0.6) });
        rebuild();
    });

    // ⚠️ Не голый addEventListener: на медленной сети человек успевает выбрать
    // файл ДО того, как модуль подключился, событие уходит в никуда, и тул
    // стоит мёртвым, пока файл не выберут второй раз. `__bindUploaderFile`
    // добирает уже случившийся выбор из `el.__pendingFile`.
    const zone = $('lmArtZone');
    if (zone) {
        const take = (file, detail) => {
            lastArtFile = file || (detail && detail.file);
            lastInvert = false;
            onArtFile(lastArtFile);
        };
        if (typeof window.__bindUploaderFile === 'function') {
            window.__bindUploaderFile(zone, take);
        } else {
            zone.addEventListener('tool-uploader:file', (e) => take(e.detail && e.detail.file, e.detail));
        }
    }

    // ⚠️ Ctrl+Z и Ctrl+Y — ожидаемые клавиши в любом редакторе. Не перехватываем
    // их, когда человек печатает: там отмена принадлежит полю.
    document.addEventListener('keydown', (e) => {
        const tag = (e.target && e.target.tagName) || '';
        if (/INPUT|TEXTAREA|SELECT/.test(tag)) return;
        if (!(e.ctrlKey || e.metaKey)) return;
        const kl = window.__keyLetter ? window.__keyLetter(e) : String(e.key || '').toLowerCase();
        if (kl === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
        else if (kl === 'y' || (kl === 'z' && e.shiftKey)) { e.preventDefault(); redo(); }
    });

    window.addEventListener('resize', deb(() => paint(), 160));
    document.addEventListener('tier-bootstrap:ready', syncPro);
}

let lastArtFile = null;
let lastInvert = false;

function on(id, fn) {
    const e = $(id);
    if (e) e.addEventListener('click', fn);
}

/* ── старт ───────────────────────────────────────────────────────────────── */
async function init() {
    if (!$('lmName') || !window.PdfTextFont) return;
    await window.PdfTextFont.load();
    loadHash();
    tabs();
    fontMenu();
    markCats();
    paletteChips();
    sliders();
    segments();
    setupEyedropper();
    syncInputs();
    bind();
    syncPro();
    await rebuild();
    rollVariants(false);

    // Опора для сторожей: состояние видно снаружи без чтения пикселей.
    window.__lmState = state;
    window.__lmSvg = svgText;
    // Опора для сторожа: тот же путь, которым идёт кнопка выдачи — сперва
    // дождаться правки, потом рисовать. Без него сторож проверял бы svgText,
    // то есть ровно ту функцию, которая о правке и не знала.
    window.__lmSettleAndSvg = async (size, opts) => { await settle(); return svgText(size, opts); };
    window.__lmScene = () => scene;
    window.__lmIconScene = () => iconScene;
    window.__lmSheet = sheetSvg;
    window.__lmSetArt = async (f) => { lastArtFile = f; await onArtFile(f); };
    window.__lmArt = () => art;
    // Опора для сторожа: значок набора строится тем же путём, что уедет в файл.
    window.__lmIcons = ICONS;
    window.__lmIconPng = iconPng;
    window.__lmKit = { AVATARS: KIT.AVATARS, COVERS: KIT.COVERS, VARIANTS: KIT.VARIANTS };
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
